'use client';

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Flex, Box, VStack } from '@skin-seoul/design-system';
import { Carousel, type CarouselApi } from '@/components/ui/Carousel';
import Image from 'next/image';
import SliderDot from './components/SliderDot';
import { useProductService, useRegionCurrency } from '@skin-seoul/service';
import { useParams } from 'next/navigation';
const DESKTOP_IMAGE_SIZE = 410;
const THUMBNAIL_SIZE = 60;
interface ProductGalleryImageProps {
  image: string;
  alt: string;
  priority: boolean;
  fill?: boolean;
  width?: number;
  height?: number;
  objectFit?: 'cover' | 'contain';
  aspectRatio?: number;
}
const ProductGalleryImage = ({
  image,
  alt,
  priority,
  fill,
  width,
  height,
  objectFit,
  aspectRatio
}: ProductGalleryImageProps) => {
  return <Box width="100%" position="relative" aspectRatio={aspectRatio} data-sentry-element="Box" data-sentry-component="ProductGalleryImage" data-sentry-source-file="ProductImageGallerySection.tsx">
      <Image src={image} alt={alt} fill={fill} width={!fill ? width : undefined} height={!fill ? height : undefined} priority={priority} loading={priority ? 'eager' : 'lazy'} objectFit={objectFit} data-sentry-element="Image" data-sentry-source-file="ProductImageGallerySection.tsx" />
    </Box>;
};
const ProductImageGallerySection = () => {
  const {
    useGetProductsDetail
  } = useProductService();
  const {
    currency
  } = useRegionCurrency();
  const params = useParams();
  const {
    data: {
      product
    }
  } = useGetProductsDetail({
    slug: params.slug as string,
    currency
  });
  const [mobileApi, setMobileApi] = useState<CarouselApi | null>(null);
  const [desktopApi, setDesktopApi] = useState<CarouselApi | null>(null);
  const [mobileSelectedIndex, setMobileSelectedIndex] = useState(0);
  const [desktopSelectedIndex, setDesktopSelectedIndex] = useState(0);
  const thumbnailContainerRef = useRef<HTMLDivElement>(null);
  const images = product.productImageUrls;
  const handleMobileSelect = useCallback(() => {
    if (mobileApi) {
      setMobileSelectedIndex(mobileApi.selectedScrollSnap());
    }
  }, [mobileApi]);
  const handleDesktopSelect = useCallback(() => {
    if (desktopApi) {
      setDesktopSelectedIndex(desktopApi.selectedScrollSnap());
    }
  }, [desktopApi]);
  const handleMobileDotClick = useCallback((index: number) => {
    mobileApi?.scrollTo(index);
  }, [mobileApi]);
  const handleThumbnailClick = useCallback((index: number) => {
    desktopApi?.scrollTo(index);
  }, [desktopApi]);
  useEffect(() => {
    if (!mobileApi) {
      return;
    }
    setMobileSelectedIndex(mobileApi.selectedScrollSnap());
    mobileApi.on('select', handleMobileSelect);
    return () => {
      mobileApi.off('select', handleMobileSelect);
    };
  }, [mobileApi, handleMobileSelect]);
  useEffect(() => {
    if (!desktopApi) {
      return;
    }
    setDesktopSelectedIndex(desktopApi.selectedScrollSnap());
    desktopApi.on('select', handleDesktopSelect);
    return () => {
      desktopApi.off('select', handleDesktopSelect);
    };
  }, [desktopApi, handleDesktopSelect]);
  useEffect(() => {
    if (thumbnailContainerRef.current && images.length > 1) {
      const thumbnailHeight = THUMBNAIL_SIZE + 8;
      const scrollTop = desktopSelectedIndex * thumbnailHeight;
      thumbnailContainerRef.current.scrollTo({
        top: scrollTop,
        behavior: 'smooth'
      });
    }
  }, [desktopSelectedIndex, images.length]);
  return <Box position="relative" background="white" padding={{
    base: 4,
    tablet: 6
  }} borderRadius={8} width="100%" height="100%" data-sentry-element="Box" data-sentry-component="ProductImageGallerySection" data-sentry-source-file="ProductImageGallerySection.tsx">
      {/* Mobile Layout */}
      <VStack gap={{
      base: 4,
      tablet: 8
    }} width="100%" height="100%" alignItems="center" justifyContent="center" display={{
      base: 'flex',
      tablet: 'none'
    }} data-sentry-element="VStack" data-sentry-source-file="ProductImageGallerySection.tsx">
        <Box width="100%" borderRadius={12} overflow="hidden" data-sentry-element="Box" data-sentry-source-file="ProductImageGallerySection.tsx">
          <Carousel opts={{
          loop: true,
          align: 'center'
        }} setApi={setMobileApi} data-sentry-element="Carousel" data-sentry-source-file="ProductImageGallerySection.tsx">
            <Carousel.Content data-sentry-element="Carousel.Content" data-sentry-source-file="ProductImageGallerySection.tsx">
              {images.map((image: string, index: number) => <Carousel.Item key={index} style={{
              paddingLeft: '0'
            }}>
                  <ProductGalleryImage image={image} alt={`${product.name} - Image ${index + 1}`} fill priority={index === 0} objectFit={index === 0 ? 'cover' : 'contain'} aspectRatio={1} />
                </Carousel.Item>)}
            </Carousel.Content>
          </Carousel>
        </Box>

        {images.length > 1 && <Box display={{
        base: 'block',
        tablet: 'none'
      }} width="100%">
            <Flex justifyContent="center" gap={1}>
              {images.map((_, index) => <SliderDot key={index} isSelected={index === mobileSelectedIndex} onClick={() => handleMobileDotClick(index)} size="md" />)}
            </Flex>
          </Box>}
      </VStack>

      {/* Desktop Layout */}
      <Flex display={{
      base: 'none',
      tablet: 'flex'
    }} gap={16} alignItems="center" justifyContent="center" width="100%" height="100%" data-sentry-element="Flex" data-sentry-source-file="ProductImageGallerySection.tsx">
        {images.length > 1 && <VStack ref={thumbnailContainerRef} gap={2} height={`${DESKTOP_IMAGE_SIZE}px`} overflowY="auto" paddingRight="4px" justifyContent={images.length < 5 ? 'center' : 'flex-start'} css={{
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}>
            {images.map((image, index) => <Box key={index} width={`${THUMBNAIL_SIZE}px`} borderRadius={4} overflow="hidden" border="1px solid" borderColor={index === desktopSelectedIndex ? 'primary.400' : 'transparent'} cursor="pointer" transition="all 0.2s" _hover={{
          borderColor: 'primary.300'
        }} onClick={() => handleThumbnailClick(index)} position="relative" flexShrink={0}>
                <ProductGalleryImage image={image} alt={`${product.name} - Image ${index + 1}`} width={THUMBNAIL_SIZE} height={THUMBNAIL_SIZE} priority={true} />
              </Box>)}
          </VStack>}

        <Box width={`${DESKTOP_IMAGE_SIZE}px`} borderRadius={12} overflow="hidden" data-sentry-element="Box" data-sentry-source-file="ProductImageGallerySection.tsx">
          <Carousel opts={{
          loop: true,
          align: 'center'
        }} setApi={setDesktopApi} data-sentry-element="Carousel" data-sentry-source-file="ProductImageGallerySection.tsx">
            <Carousel.Content data-sentry-element="Carousel.Content" data-sentry-source-file="ProductImageGallerySection.tsx">
              {images.map((image: string, index: number) => <Carousel.Item key={index} style={{
              paddingLeft: '0'
            }}>
                  <ProductGalleryImage image={image} alt={`${product.name} - Image ${index + 1}`} width={DESKTOP_IMAGE_SIZE} height={DESKTOP_IMAGE_SIZE} priority={index === 0} />
                </Carousel.Item>)}
            </Carousel.Content>
          </Carousel>
        </Box>
      </Flex>
    </Box>;
};
export default ProductImageGallerySection;